import React from 'react';
import { Bar, CartesianGrid, ComposedChart, Line, ReferenceLine, Tooltip, XAxis, YAxis } from 'recharts';

import { Card } from '@/components/ui/card';
import { formatDate } from 'utils';

import { formatCompactCurrency, formatFullCurrency } from '../../reporting/format';
import {
    NAV_CHART_AXIS_TICK_COLOR,
    NAV_CHART_AXIS_TICK_FONT_SIZE,
    NAV_CHART_GRID_COLOR,
    NAV_CHART_LINE_WIDTH,
    NAV_CHART_MONTH_LABEL_FORMAT,
} from '../constants';
import { MonthlyNetFlow } from '../transactions';

interface NetInflowOutflowProps {
    flows: MonthlyNetFlow[];
    isLoading?: boolean;
}

// Axis ticks keep a leading minus rather than the parentheses used elsewhere: it reads unambiguously
// on an axis that spans both sides of zero.
const axisTick = (value: number) => (value < 0 ? `-${formatCompactCurrency(-value)}` : formatCompactCurrency(value));

export const NetInflowOutflow = ({ flows, isLoading }: NetInflowOutflowProps) => {
    // Card shell, title, legend and footnote match the Market Value chart (NavChart).
    return (
        <Card className="border-border flex min-w-0 flex-col rounded-2xl bg-white p-4 lg:p-6">
            <h3 className="font-heading text-cocoa-1000 text-xl">External flows</h3>
            <div className="mt-6 h-72 w-full">
                {isLoading ? (
                    <div className="bg-cocoa-100 h-full w-full animate-pulse rounded-md" />
                ) : flows.length === 0 ? (
                    <div className="text-cocoa-400 flex h-full w-full items-center justify-center text-sm">
                        No cash flow to display.
                    </div>
                ) : (
                    <ComposedChart
                        className="[&_*]:!outline-none"
                        responsive
                        style={{ width: '100%', height: '100%' }}
                        data={flows}
                        // Stacks inflows up and outflows down from zero within the same month's column.
                        stackOffset="sign"
                        margin={{ top: 8, right: 8, left: 16, bottom: 0 }}
                    >
                        <CartesianGrid strokeDasharray="3 3" stroke={NAV_CHART_GRID_COLOR} vertical={false} />
                        <XAxis
                            dataKey="yearMonth"
                            tickFormatter={(yearMonth: string) => formatDate(yearMonth, NAV_CHART_MONTH_LABEL_FORMAT)}
                            tick={{ fontSize: NAV_CHART_AXIS_TICK_FONT_SIZE, fill: NAV_CHART_AXIS_TICK_COLOR }}
                            axisLine={false}
                            tickLine={false}
                            minTickGap={24}
                        />
                        <YAxis
                            orientation="right"
                            tickFormatter={axisTick}
                            tick={{ fontSize: NAV_CHART_AXIS_TICK_FONT_SIZE, fill: NAV_CHART_AXIS_TICK_COLOR }}
                            axisLine={false}
                            tickLine={false}
                            width={72}
                        />
                        <Tooltip
                            formatter={(value, name) => [
                                formatFullCurrency(Array.isArray(value) ? value[0] : value),
                                name,
                            ]}
                            labelFormatter={(yearMonth) => formatDate(String(yearMonth), 'MMMM yyyy')}
                            // Recharts sorts rows by name by default; keep the legend's order instead.
                            itemSorter={(item) => ['inflow', 'outflow', 'net'].indexOf(String(item.dataKey))}
                            cursor={{ fill: 'rgba(0, 0, 0, 0.04)' }}
                            contentStyle={{
                                fontSize: 12,
                                borderRadius: 6,
                                border: `1px solid ${NAV_CHART_GRID_COLOR}`,
                            }}
                        />
                        {/* Outflow amounts stay negative, so their bars hang below this zero line. */}
                        <ReferenceLine y={0} stroke={NAV_CHART_AXIS_TICK_COLOR} />
                        <Bar
                            dataKey="inflow"
                            name="Inflows"
                            stackId="flow"
                            fill="var(--color-seafoam-300)"
                            radius={[2, 2, 0, 0]}
                        />
                        <Bar
                            dataKey="outflow"
                            name="Outflows"
                            stackId="flow"
                            fill="var(--color-gold-200)"
                            radius={[0, 0, 2, 2]}
                        />
                        <Line
                            dataKey="net"
                            name="Net"
                            type="monotone"
                            stroke="var(--color-cocoa-800)"
                            strokeWidth={NAV_CHART_LINE_WIDTH}
                            dot={false}
                        />
                    </ComposedChart>
                )}
            </div>
            {/* Square swatches and palette tokens as in AssetAllocation (seafoam-300 = equities). */}
            <div className="mt-3 flex flex-wrap items-center gap-6 text-[10px] font-semibold tracking-wider uppercase">
                <div className="text-cocoa-500 flex items-center gap-2">
                    <span className="bg-seafoam-300 inline-block h-2.5 w-2.5 shrink-0 rounded-sm" />
                    Inflows
                </div>
                <div className="text-cocoa-500 flex items-center gap-2">
                    <span className="bg-gold-200 inline-block h-2.5 w-2.5 shrink-0 rounded-sm" />
                    Outflows
                </div>
                <div className="text-cocoa-500 flex items-center gap-2">
                    <span className="bg-cocoa-800 h-0.5 w-4 shrink-0 self-center" />
                    Net
                </div>
            </div>
            <div className="text-cocoa-400 mt-2 text-xs">
                Inflows and outflows are split by each transaction&apos;s amount; dividends and interest count as
                inflows.
            </div>
        </Card>
    );
};
